import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import JobApplicationForm from './JobApplicationForm';

function ProductOwner() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const position = {
    title: 'Product Owner',
    department: 'Product',
    location: 'Worldwide (Remote)',
    type: 'Full-Time',
    salaryRange: '$700-$1,200 USD',
    about: 'SellAbroad is a dynamic platform dedicated to empowering businesses to expand internationally. We provide seamless cross-border e-commerce solutions—from localized storefronts and multi-currency payment processing to international shipping logistics and market insights. Our mission is to simplify global selling so that businesses can focus on what they do best: creating exceptional products and services.',
    description: "As a Product Owner at SellAbroad, you will be the driving force behind the evolution of our international e-commerce platform. You'll work closely with cross-functional teams including design, engineering, marketing, and international operations to develop a product roadmap that meets the needs of global sellers. Your goal is to deliver an outstanding user experience that simplifies and accelerates international market entry.",
    responsibilities: [
      "Develop and communicate a clear product vision and strategy aligned with SellAbroad's global mission",
      'Create a dynamic product roadmap prioritizing features and enhancements based on market trends, user feedback, and business objectives',
      'Serve as the primary liaison between technical teams, business stakeholders, and end users, ensuring alignment with company strategy',
      'Work within an Agile framework to manage the product backlog, write user stories, and define acceptance criteria',
      'Conduct competitive analysis and gather insights from global markets to identify trends, challenges, and opportunities',
      'Validate feature ideas and measure product performance using data-driven approaches',
      'Collaborate with UX/UI designers to ensure platform usability across diverse regions',
      'Partner with marketing and sales teams to understand customer pain points and optimize the customer journey',
      'Monitor key performance indicators (KPIs) and iterate on product features for enhanced performance, scalability, and internationalization'
    ],
    requirements: [
      "3–5 years' experience in product management, preferably in e-commerce, SaaS, or international business platforms",
      'Proven track record in launching and managing digital products from concept through release',
      'Strong understanding of Agile methodologies (e.g., Scrum or Kanban)',
      'Excellent communication and stakeholder management skills',
      'Ability to translate business needs into technical requirements and actionable user stories',
      'Analytical mindset with a strong foundation in data-driven decision-making',
      'Familiarity with cross-border e-commerce challenges and opportunities (e.g., localization, multi-currency, international logistics)',
      'Proficient in digital product development tools and project management software',
      "Bachelor's degree in Business, Computer Science, Engineering, or a related field (advanced degrees or relevant certifications are a plus)"
    ],
    benefits: [
      'A collaborative and innovative work environment focused on global impact',
      'Opportunities for professional growth and advancement',
      'Competitive salary and benefits package',
      'The chance to be a key player in shaping the future of international e-commerce',
      'Flexible remote work arrangement',
      'Regular team building activities and professional development opportunities'
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Link
            to="/careers"
            className="text-[#0049ac] hover:text-[#0049ac]/90 flex items-center"
          >
            ← Back to Careers
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-xl overflow-hidden">
              <div className="p-8">
                <div className="mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">{position.title}</h1>
                  <div className="flex items-center gap-2 mb-6">
                    <p className="text-gray-500">{position.department} • {position.location} • {position.type}</p>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {position.salaryRange}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {position.type}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                      {position.department}
                    </span>
                  </div>
                </div>

                <div className="prose max-w-none">
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">About SellAbroad</h2>
                    <p className="text-gray-600">{position.about}</p>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">Description</h2>
                    <p className="text-gray-600">{position.description}</p>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">Responsibilities</h2>
                    <ul className="list-disc pl-5 space-y-6">
                      {position.responsibilities.map((responsibility, index) => (
                        <li key={index}>{responsibility}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">Requirements</h2>
                    <ul className="list-disc pl-5">
                      {position.requirements.map((requirement, index) => (
                        <li key={index}>{requirement}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">Benefits</h2>
                    <ul className="list-disc pl-5">
                      {position.benefits.map((benefit, index) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white rounded-lg shadow-xl p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Apply for this Position</h2>
                <p className="text-gray-600 mb-6">
                  Join our team and help transform e-commerce in the MENA region.
                </p>
                <Link
                  to="/careers/product-owner/apply"
                  className="w-full flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#0049ac] hover:bg-[#0049ac]/90 transition-all duration-200"
                >
                  Apply Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductOwner;